import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, Filter, X, ChevronDown, Sparkles, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { getJobs, applyJob } from '../services/api';
import { JobCard } from '../components/cards/JobCard';
import { Briefcase, GraduationCap } from 'lucide-react';

export const FindJob = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // User location for distance calculation
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationReady, setLocationReady] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [salaryRange, setSalaryRange] = useState([0, 100000]);
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [distanceFilter, setDistanceFilter] = useState('all');

  // Get user's location on mount
  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [searchQuery, filterType, sortBy, locationReady]);

  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
          setLocationReady(true);
        },
        (error) => {
          console.log('Location access denied or unavailable');
          setLocationLoading(false);
          setLocationReady(true);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
      );
    } else {
      setLocationLoading(false);
      setLocationReady(true);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchQuery,
        job_type: filterType,
        sort_by: sortBy
      };

      // Add user location for distance calculation if available
      if (userLocation) {
        params.user_lat = userLocation.lat;
        params.user_lng = userLocation.lng;
      }

      const data = await getJobs(params);
      // Handle both direct array response and pagination object response
      const jobsList = Array.isArray(data)
        ? data
        : (data.jobs || data.data || []);
      setJobs(jobsList);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setLocationFilter('');
    setSalaryRange([0, 100000]);
    setExperienceFilter('all');
    setSubjectFilter('');
    setSortBy('recent');
    setDistanceFilter('all');
  };

  // Filter jobs locally for advanced filters
  const filteredJobs = jobs.filter(job => {
    // Distance filter
    if (distanceFilter !== 'all' && userLocation) {
      const maxDist = parseInt(distanceFilter);
      const jobDist = job.distance_km;
      if (jobDist === null || jobDist === undefined || jobDist > maxDist) return false;
    }
    if (locationFilter && !job.location?.toLowerCase().includes(locationFilter.toLowerCase())) return false;
    if (subjectFilter && !job.subjects?.some(s => s.toLowerCase().includes(subjectFilter.toLowerCase()))) return false;
    return true;
  });

  const handleApply = async (jobId, jobTitle) => {
    try {
      const applicationData = {
        applicant_name: "Demo Applicant",
        applicant_email: "applicant@example.com",
        applicant_phone: "+91 9876543210",
        cover_letter: "I am interested in this position."
      };

      await applyJob(jobId, applicationData);

      toast({
        title: "Application Submitted!",
        description: `Your application for ${jobTitle} has been submitted successfully.`,
      });
    } catch (error) {
      console.error('Error applying for job:', error);
      toast({
        title: "Application Failed",
        description: "Unable to submit application. Please try again.",
        variant: "destructive",
      });
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-3">
            Find Your Dream Teaching Job
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover exciting teaching opportunities across India. Connect with top educational institutions.
          </p>
        </div>

        {/* Search & Filters */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Main Search Row */}
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search jobs, subjects, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-48 h-12">
                  <SelectValue placeholder="Job Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Full-time">Full-time</SelectItem>
                  <SelectItem value="Part-time">Part-time</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-48 h-12">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="salary">Highest Salary</SelectItem>
                  {userLocation && <SelectItem value="distance">Nearest First</SelectItem>}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="h-12 px-6"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {/* Location Status */}
            {!userLocation && !locationLoading && (
              <div className="flex items-center gap-2 text-sm text-amber-600 mb-4">
                <Navigation className="h-4 w-4" />
                <span>Enable location to see distances and sort by nearest</span>
                <Button variant="link" size="sm" onClick={getUserLocation} className="text-blue-600 p-0 h-auto">
                  Enable
                </Button>
              </div>
            )}

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="pt-4 border-t border-gray-100 mt-4">
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Location</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="City or area"
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {userLocation && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Max Distance</label>
                      <Select value={distanceFilter} onValueChange={setDistanceFilter}>
                        <SelectTrigger data-testid="distance-filter">
                          <SelectValue placeholder="Any Distance" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any Distance</SelectItem>
                          <SelectItem value="5">Within 5 km</SelectItem>
                          <SelectItem value="10">Within 10 km</SelectItem>
                          <SelectItem value="25">Within 25 km</SelectItem>
                          <SelectItem value="50">Within 50 km</SelectItem>
                          <SelectItem value="100">Within 100 km</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Subject</label>
                    <div className="relative">
                      <GraduationCap className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="e.g., Mathematics"
                        value={subjectFilter}
                        onChange={(e) => setSubjectFilter(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Experience Level</label>
                    <Select value={experienceFilter} onValueChange={setExperienceFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Any Experience" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any Experience</SelectItem>
                        <SelectItem value="fresher">Fresher</SelectItem>
                        <SelectItem value="1-2">1-2 years</SelectItem>
                        <SelectItem value="3-5">3-5 years</SelectItem>
                        <SelectItem value="5+">5+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Button variant="ghost" onClick={clearFilters} className="text-gray-600">
                    <X className="h-4 w-4 mr-2" />
                    Clear all filters
                  </Button>
                  <Button onClick={fetchJobs} className="bg-blue-600 hover:bg-blue-700">
                    Apply Filters
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            Showing <span className="font-bold text-gray-900">{filteredJobs.length}</span> job openings
          </p>
          <Select defaultValue="relevance">
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Most Relevant</SelectItem>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="salary-high">Highest Salary</SelectItem>
              <SelectItem value="salary-low">Lowest Salary</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Finding the best jobs for you...</p>
          </div>
        )}

        {/* Jobs List */}
        {!loading && filteredJobs.length > 0 && (
          <div className="space-y-6">
            {filteredJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredJobs.length === 0 && (
          <Card className="text-center py-16 border-0 shadow-lg">
            <CardContent>
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <Briefcase className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filters to find more opportunities</p>
              <Button onClick={clearFilters} variant="outline">
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
